import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "@emotion/styled";
import {
  Plus,
  Minus,
  Search,
  X,
  Loader2,
  ShoppingCart,
  PackagePlus,
} from "lucide-react";
import { Product, Order } from "@apis/index";
import theme from "@utils/theme";
import { toast } from "react-toastify";
import Button from "@components/button";
import { Helper } from "@utils/index";
import useConfig from "@hooks/useConfig";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import { useModal } from "@context/ModalContext";
import CreateProductModal from "./CreateProductModal";

interface EditOrderModalProps {
  order: any;
  onSuccess: () => void;
}

const EditOrderModal = ({ order, onSuccess }: EditOrderModalProps) => {
  const { showModal, hideModal } = useModal();
  const { bucketUrl } = useConfig();
  const { settings } = useGeneralSettings();
  const [searchTerm, setSearchTerm] = useState("");
  const [products, setProducts] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [editCart, setEditCart] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectingAlternativeFor, setSelectingAlternativeFor] = useState<
    string | number | null
  >(null);

  // Initialize edit cart with current order items
  useEffect(() => {
    if (order?.items) {
      setEditCart(
        order.items.map((item: any) => ({
          productId: item.product?.id,
          name: item.product?.name,
          image: item.product?.images?.[0]?.image,
          price: item.product?.price || 0,
          quantity: item.quantity,
          variationId: item.infos?.variationId || null,
          isAvailable: item.isAvailable !== false,
          alternativeProduct: item.alternativeProduct || null,
        })),
      );
    }
  }, [order]);

  // Fetch products for the store
  const fetchProducts = useCallback(
    async (search = "") => {
      if (!order?.storeId) return;
      try {
        setLoadingProducts(true);
        const res = await Product.list({
          params: {
            storeId: order.storeId,
            search: search,
            limit: 20,
          },
        });
        if (res?.items) {
          setProducts(res.items);
        }
      } catch (err) {
        console.error("Failed to fetch products", err);
      } finally {
        setLoadingProducts(false);
      }
    },
    [order?.storeId],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProducts(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchProducts]);

  const handleAddToCart = (product: any) => {
    if (selectingAlternativeFor) {
      setEditCart((prev) =>
        prev.map((item) => {
          const match =
            item.productId === selectingAlternativeFor ||
            item.customName === selectingAlternativeFor;
          return match
            ? {
                ...item,
                alternativeProduct: {
                  id: product.id,
                  name: product.name,
                  price: product.price,
                  image: product.images?.[0]?.image,
                },
              }
            : item;
        }),
      );
      setSelectingAlternativeFor(null);
      toast.info(`Assigned ${product.name} as alternative`);
      return;
    }

    setEditCart((prev) => {
      const existing = prev.find((item) => item.productId === product.id);
      if (existing) {
        return prev.map((item) =>
          item.productId === product.id
            ? { ...item, quantity: item.quantity + 1, isAvailable: true }
            : item,
        );
      }
      return [
        ...prev,
        {
          productId: product.id,
          name: product.name,
          image: product.images?.[0]?.image,
          price: product.price,
          quantity: 1,
          variationId: null,
          isAvailable: true,
          alternativeProduct: null,
        },
      ];
    });
  };

  const handleToggleAvailability = (
    productId: number | string,
    isCustom = false,
  ) => {
    setEditCart((prev) =>
      prev.map((item) => {
        const match = isCustom
          ? item.customName === productId && !item.productId
          : item.productId === productId;

        return match ? { ...item, isAvailable: !item.isAvailable } : item;
      }),
    );
  };

  const handleAddCustomItem = () => {
    if (!searchTerm) return;

    if (selectingAlternativeFor) {
      setEditCart((prev) =>
        prev.map((item) => {
          const match =
            item.productId === selectingAlternativeFor ||
            item.customName === selectingAlternativeFor;
          return match
            ? {
                ...item,
                alternativeProduct: {
                  id: null,
                  customName: searchTerm,
                  name: searchTerm,
                  price: 0,
                  image: null,
                },
              }
            : item;
        }),
      );
      setSelectingAlternativeFor(null);
      setSearchTerm("");
      toast.info(`Assigned "${searchTerm}" as custom alternative`);
      return;
    }

    setEditCart((prev) => [
      ...prev,
      {
        productId: null,
        customName: searchTerm,
        name: searchTerm,
        image: null,
        price: 0,
        quantity: 1,
        variationId: null,
        isAvailable: false,
        alternativeProduct: null,
      },
    ]);
    setSearchTerm("");
    toast.info(`Added custom requested item: ${searchTerm}`);
  };

  const handleUpdateQuantity = (
    id: number | string,
    delta: number,
    isCustom = false,
  ) => {
    setEditCart((prev) =>
      prev
        .map((item) => {
          const match = isCustom
            ? item.customName === id && !item.productId
            : item.productId === id;

          return match
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item;
        })
        .filter((item) => item.quantity > 0),
    );
  };

  const handleSubmit = async () => {
    if (editCart.length === 0) {
      toast.error("Cart cannot be empty");
      return;
    }
    try {
      setSubmitting(true);
      const items = editCart.map((item) => ({
        productId: item.productId,
        customName: item.customName,
        quantity: item.quantity,
        price: item.price,
        variationId: item.variationId,
        isAvailable: item.isAvailable,
        alternativeProduct: item.alternativeProduct,
      }));
      if (order?.module?.type === "pharma") {
        await Order.sendInvoice(order.id, items);
      } else {
        await Order.editOrder(order.id, items);
      }
      toast.success("Order updated successfully");
      onSuccess();
      hideModal();
    } catch (err: any) {
      toast.error(err.message || "Failed to update order");
    } finally {
      setSubmitting(false);
    }
  };

  const handleImagePreview = (imageUrl: string) => {
    showModal(
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "#000",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <img
          src={imageUrl}
          alt="Prescription Preview"
          style={{
            maxWidth: "100%",
            maxHeight: "85vh",
            objectFit: "contain",
          }}
        />
      </div>,
      { width: "auto", padding: "0" },
    );
  };

  const calculateTotal = () => {
    return editCart.reduce(
      (sum, item) =>
        item.isAvailable ? sum + item.price * item.quantity : sum,
      0,
    );
  };

  const handleProductCreated = (created: any) => {
    setEditCart((prev) => [
      ...prev,
      {
        productId: created.id,
        name: created.name,
        image: created.images?.[0]?.image || null,
        price: created.price,
        quantity: 1,
        variationId: null,
      },
    ]);
    fetchProducts(searchTerm);
  };

  return (
    <ModalLayout>
      <Header>
        <Title>Edit Order #{order.id}</Title>
        <CloseButton onClick={hideModal}>
          <X size={20} />
        </CloseButton>
      </Header>

      <Content>
        {order?.prescriptionFiles && order.prescriptionFiles.length > 0 && (
          <PrescriptionSidebar>
            <SidebarTitle>Prescription</SidebarTitle>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {order.prescriptionFiles.map((file: any, i: number) => (
                <img
                  key={i}
                  src={`${bucketUrl}/${file}`}
                  alt="Prescription"
                  style={{
                    width: "100%",
                    height: "auto",
                    minHeight: "150px",
                    maxHeight: "300px",
                    objectFit: "contain",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    cursor: "pointer",
                    transition: "transform 0.2s",
                  }}
                  onClick={() => handleImagePreview(`${bucketUrl}/${file}`)}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.transform = "scale(1.05)")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.transform = "scale(1)")
                  }
                />
              ))}
            </div>
          </PrescriptionSidebar>
        )}

        <LeftPane>
          <TopBar>
            <SearchWrapper>
              <Search size={18} className="search-icon" />
              <SearchInput
                placeholder="Search products in this store..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchWrapper>
            <ToggleBtn onClick={() => setIsCreateModalOpen(true)}>
              <PackagePlus size={14} /> Create Product
            </ToggleBtn>
          </TopBar>

          {selectingAlternativeFor && (
            <SelectingBanner>
              <span>
                👆 Click a product below to assign it as an alternative
              </span>
              <CancelSelectBtn onClick={() => setSelectingAlternativeFor(null)}>
                Cancel
              </CancelSelectBtn>
            </SelectingBanner>
          )}

          <ProductList>
            {loadingProducts ? (
              <LoadingState>
                <Loader2 className="animate-spin" />
                <p>Loading products...</p>
              </LoadingState>
            ) : products.length > 0 ? (
              <Grid>
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    onClick={() => handleAddToCart(product)}
                  >
                    <ProductImage
                      src={
                        product.images?.[0]?.image
                          ? `${bucketUrl}/${product.images[0].image}`
                          : "/placeholder.png"
                      }
                      alt={product.name}
                    />
                    <ProductInfo>
                      <ProductName title={product.name}>
                        {product.name}
                      </ProductName>
                      <ProductPrice>
                        {Helper.formatCurrency(product.price, settings)}
                      </ProductPrice>
                    </ProductInfo>
                    <AddIcon>
                      <Plus size={14} />
                    </AddIcon>
                  </ProductCard>
                ))}
              </Grid>
            ) : (
              <EmptyState>
                <p>No products found in this store.</p>
                {searchTerm && (
                  <Button
                    title={
                      selectingAlternativeFor
                        ? `Add "${searchTerm}" as custom alternative`
                        : `Add "${searchTerm}" as custom item`
                    }
                    onClick={handleAddCustomItem}
                  />
                )}
              </EmptyState>
            )}
          </ProductList>
        </LeftPane>

        {isCreateModalOpen && (
          <SubModalOverlay onClick={() => setIsCreateModalOpen(false)}>
            <div
              style={{ width: "700px", zIndex: 1001 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CreateProductModal
                storeId={order.storeId}
                moduleId={order.module?.id}
                hideModal={() => setIsCreateModalOpen(false)}
                onProductCreated={handleProductCreated}
              />
            </div>
          </SubModalOverlay>
        )}

        <RightPane>
          <CartTitle>Updated Items</CartTitle>
          <CartList>
            {editCart.map((item, idx) => {
              const itemKey =
                item.productId || `custom-${item.customName}-${idx}`;
              return (
                <CartItem key={itemKey} isAvailable={item.isAvailable}>
                  <CartItemImage
                    src={
                      item.image
                        ? `${bucketUrl}/${item.image}`
                        : "/placeholder.png"
                    }
                  />
                  <CartItemDetails>
                    <CartItemName>{item.name || item.customName}</CartItemName>
                    <CartItemPrice>
                      {item.productId ? (
                        Helper.formatCurrency(item.price, settings)
                      ) : (
                        <PriceInput
                          type="number"
                          placeholder="Set Price"
                          value={item.price || ""}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            const id = item.customName;
                            setEditCart((prev) =>
                              prev.map((i) =>
                                !i.productId && i.customName === id
                                  ? { ...i, price: val }
                                  : i,
                              ),
                            );
                          }}
                        />
                      )}
                    </CartItemPrice>

                    {!item.isAvailable && item.alternativeProduct && (
                      <AlternativeBadge>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            Alt:{" "}
                            {item.alternativeProduct.name ||
                              item.alternativeProduct.customName}
                            <X
                              size={10}
                              onClick={(e) => {
                                e.stopPropagation();
                                const id = item.productId || item.customName;
                                const isCustom = !item.productId;
                                setEditCart((prev) =>
                                  prev.map((i) => {
                                    const match = isCustom
                                      ? i.customName === id && !i.productId
                                      : i.productId === id;
                                    return match
                                      ? { ...i, alternativeProduct: null }
                                      : i;
                                  }),
                                );
                              }}
                            />
                          </div>
                          {!item.alternativeProduct.id && (
                            <PriceInput
                              type="number"
                              placeholder="Alt Price"
                              value={item.alternativeProduct.price || ""}
                              onChange={(e) => {
                                const val = Number(e.target.value);
                                const id = item.productId || item.customName;
                                const isCustom = !item.productId;
                                setEditCart((prev) =>
                                  prev.map((i) => {
                                    const match = isCustom
                                      ? i.customName === id && !i.productId
                                      : i.productId === id;
                                    return match
                                      ? {
                                          ...i,
                                          alternativeProduct: {
                                            ...i.alternativeProduct,
                                            price: val,
                                          },
                                        }
                                      : i;
                                  }),
                                );
                              }}
                            />
                          )}
                        </div>
                      </AlternativeBadge>
                    )}
                  </CartItemDetails>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      alignItems: "flex-end",
                    }}
                  >
                    <AvailabilityToggle
                      active={item.isAvailable}
                      onClick={() =>
                        handleToggleAvailability(
                          item.productId || item.customName,
                          !item.productId,
                        )
                      }
                    >
                      {item.isAvailable ? "Available" : "Not Available"}
                    </AvailabilityToggle>

                    {!item.isAvailable && (
                      <AltBtn
                        active={
                          selectingAlternativeFor ===
                          (item.productId || item.customName)
                        }
                        onClick={() =>
                          setSelectingAlternativeFor(
                            selectingAlternativeFor ===
                              (item.productId || item.customName)
                              ? null
                              : item.productId || item.customName,
                          )
                        }
                      >
                        {item.alternativeProduct ? "Change Alt" : "Select Alt"}
                      </AltBtn>
                    )}

                    {item.isAvailable && (
                      <QuantityControls>
                        <QtyBtn
                          onClick={() =>
                            handleUpdateQuantity(
                              item.productId || item.customName,
                              -1,
                              !item.productId,
                            )
                          }
                        >
                          <Minus size={12} />
                        </QtyBtn>
                        <QtyVal>{item.quantity}</QtyVal>
                        <QtyBtn
                          onClick={() =>
                            handleUpdateQuantity(
                              item.productId || item.customName,
                              1,
                              !item.productId,
                            )
                          }
                        >
                          <Plus size={12} />
                        </QtyBtn>
                      </QuantityControls>
                    )}
                  </div>
                </CartItem>
              );
            })}
            {editCart.length === 0 && (
              <EmptyCartState>
                <ShoppingCart size={40} />
                <p>Cart is empty. Add products from the left.</p>
              </EmptyCartState>
            )}
          </CartList>

          <Footer>
            <SummaryRow>
              <span>Subtotal:</span>
              <TotalVal>
                {Helper.formatCurrency(calculateTotal(), settings)}
              </TotalVal>
            </SummaryRow>
            <p className="note">
              * Totals and commissions will be recalculated on submit.
            </p>
            <Button
              title={submitting ? "Updating..." : "Save Changes"}
              onClick={handleSubmit}
              disabled={submitting || editCart.length === 0}
            />
          </Footer>
        </RightPane>
      </Content>
    </ModalLayout>
  );
};

export default EditOrderModal;

const ModalLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 95vh;
  width: 100%;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  &:hover {
    color: #111827;
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const LeftPane = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  padding: 24px;
  border-right: 1px solid #eee;
  overflow: hidden;
`;

const PrescriptionSidebar = styled.div`
  width: 250px;
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  padding: 12px;
  overflow-y: auto;
`;

const SidebarTitle = styled.h4`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 10px;
`;

const RightPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f9fafb;
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 10px 10px 40px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  outline: none;
  &:focus {
    border-color: ${theme.colors.primary};
  }
`;

const ProductList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 16px;
`;

const ProductCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  &:hover {
    border-color: ${theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: 6px;
  margin-bottom: 8px;
`;

const ProductInfo = styled.div`
  padding: 4px 0;
`;

const ProductName = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProductPrice = styled.p`
  margin: 4px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const AddIcon = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  background: ${theme.colors.primary};
  color: #fff;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
`;

const CartTitle = styled.h3`
  padding: 20px 20px 10px;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const CartList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px 20px;
`;

const CartItem = styled.div<{ isAvailable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;
  opacity: ${({ isAvailable }) => (isAvailable === false ? 0.7 : 1)};
  background: ${({ isAvailable }) =>
    isAvailable === false ? "#fff1f2" : "transparent"};
  margin: 0 -10px;
  padding: 12px 10px;
  border-radius: 8px;
`;

const CartItemImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
`;

const CartItemDetails = styled.div`
  flex: 1;
`;

const CartItemName = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 500;
`;

const CartItemPrice = styled.p`
  margin: 2px 0 0;
  font-size: 12px;
  color: #6b7280;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #eee;
  padding: 4px 8px;
  border-radius: 6px;
`;

const QtyBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #374151;
  display: flex;
  align-items: center;
`;

const QtyVal = styled.span`
  font-size: 14px;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
`;

const Footer = styled.div`
  padding: 20px;
  background: #fff;
  border-top: 1px solid #eee;
  .note {
    font-size: 11px;
    color: #6b7280;
    margin: 8px 0 0;
    font-style: italic;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
`;

const TotalVal = styled.span`
  color: ${theme.colors.primary};
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #6b7280;
  gap: 12px;
`;

const EmptyState = styled.p`
  text-align: center;
  color: #6b7280;
  padding: 40px 0;
`;

const EmptyCartState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
  text-align: center;
  gap: 12px;
  p {
    font-size: 14px;
    margin: 0;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 0 12px;
`;

const ToggleBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  &:hover {
    background: #e5e7eb;
  }
`;

const CreateFormTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  flex: 1;
`;

const CreateForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px dashed #d1d5db;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;

  label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
  }

  input[type="text"],
  input[type="number"] {
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;
    &:focus {
      border-color: ${theme.colors.primary};
    }
  }

  input[type="file"] {
    font-size: 13px;
    color: #6b7280;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 12px;
`;

const SubModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
`;
const AvailabilityToggle = styled.button<{ active: boolean }>`
  padding: 4px 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  border-radius: 4px;
  border: 1px solid ${({ active }) => (active ? "#10b981" : "#ef4444")};
  background: ${({ active }) => (active ? "#ecfdf5" : "#fef2f2")};
  color: ${({ active }) => (active ? "#059669" : "#dc2626")};
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    filter: brightness(0.95);
  }
`;

const AltBtn = styled.button<{ active?: boolean }>`
  padding: 4px 8px;
  font-size: 10px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid
    ${({ active }) => (active ? theme.colors.primary : "#d1d5db")};
  background: ${({ active }) => (active ? theme.colors.primary : "#fff")};
  color: ${({ active }) => (active ? "#fff" : "#374151")};
  cursor: pointer;
  &:hover {
    border-color: ${theme.colors.primary};
  }
`;

const AlternativeBadge = styled.div`
  margin-top: 4px;
  font-size: 10px;
  background: #e0f2fe;
  color: #0369a1;
  padding: 2px 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  svg {
    cursor: pointer;
    &:hover {
      color: #ef4444;
    }
  }
`;

const SelectingBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 500;
  color: #92400e;
  animation: pulse 1.5s ease-in-out infinite;
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
  }
`;

const CancelSelectBtn = styled.button`
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid #dc2626;
  background: #fff;
  color: #dc2626;
  cursor: pointer;
  &:hover {
    background: #fef2f2;
  }
`;

const PriceInput = styled.input`
  width: 80px;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  &:focus {
    border-color: ${theme.colors.primary};
  }
`;
